import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';

// 게임 카테고리 옵션
const GAME_CATEGORIES = [
  'Icebreaking',
  'Language Game'
];

// 난이도 옵션
const DIFFICULTY_LEVELS = [
  { value: 'easy', label: 'Easy', color: 'text-green-600' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-600' },
  { value: 'hard', label: 'Hard', color: 'text-red-600' }
];

// 이벤트 크기 옵션 - 게임이 적합한 이벤트 규모
const EVENT_SIZE_OPTIONS = [
  { value: 'SMALL', label: '👥 Small Event (~40 people)', min: 1, max: 40 },
  { value: 'MEDIUM', label: '👨‍👩‍👧‍👦 Medium Event (40-60 people)', min: 40, max: 60 },
  { value: 'LARGE', label: '🎪 Large Event (60+ people)', min: 60, max: 999 }
];

export default function AdminGamesPage() {
  const { user } = useUser();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeTab, setActiveTab] = useState('games'); // 'games' or 'analytics'
  const [feedbackData, setFeedbackData] = useState([]);
  const [showPerformanceCriteria, setShowPerformanceCriteria] = useState(false);
  const [sortField, setSortField] = useState('name'); // 'name', 'category', 'played', 'rating', 'performance'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  const [isMobile, setIsMobile] = useState(false);

  // Game Analytics 탭용 상태
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [analyticsActiveTab, setAnalyticsActiveTab] = useState('current'); // 'current' or 'past'
  const [selectedEventId, setSelectedEventId] = useState('all'); // 'all' for 전체, 특정 이벤트 ID
  const [selectedBranch, setSelectedBranch] = useState('all'); // 'all', 'NCCU', 'NTU', 'Taipei'
  const [recentEventsCount, setRecentEventsCount] = useState('all'); // 'all', '3', '5', '10'
  const [collapsedCategories, setCollapsedCategories] = useState({
    customFeedback: false // Custom Feedback 섹션 접기/펴기 상태
  }); // 카테고리 접기/펴기 상태

  // 폼 데이터 상태
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    instructions: '',
    videoUrl: '',
    category: '',
    eventSize: 'SMALL',        // 'SMALL', 'MEDIUM', 'LARGE'
    totalMinPlayers: 1,        // 총 최소 인원 (eventSize에 따라 자동 설정)
    totalMaxPlayers: 40,       // 총 최대 인원 (eventSize에 따라 자동 설정)
    recommendedTeamSize: '',   // 추천 팀 인원 (예: "6-8명")
    difficultyLevel: 'medium',
    durationMinutes: ''
  });

  // 권한 확인 - 모든 스태프 역할 허용
  if (!user?.isLoggedIn || !['STAFF', 'LEADER', 'PRESIDENT', 'ADMIN'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 게임 목록 가져오기
  useEffect(() => {
    fetchGames();
    fetchFeedbackData(); // 이제 이벤트 데이터도 함께 로드함
  }, []);

  // 피드백 데이터가 변경될 때마다 게임 통계 업데이트
  useEffect(() => {
    if (games.length > 0 && feedbackData.length >= 0) {
      const gamesWithStats = games.map(game => {
        // ✅ game.gameId (String)를 사용해야 Feedback의 gameId와 매칭됨
        const gameId = game.gameId || game.id?.toString();
        const playedCount = calculateGamePlayCount(gameId);
        const averageRating = calculateGameAverageRating(gameId);
        return {
          ...game,
          playedCount,
          averageRating
        };
      });
      setGames(gamesWithStats);
    }
  }, [feedbackData]);

  // 이벤트 데이터 가져오기
  const fetchEventsData = async () => {
    try {
      const [eventsResponse, pastEventsResponse] = await Promise.all([
        axios.get('/api/admin/events'),
        axios.get('/api/feedback/archived-events').catch(() => ({ data: [] }))
      ]);
      
      // 유효한 이벤트만 필터링
      const validEvents = (eventsResponse.data || []).filter(event => 
        event.id && event.id !== 'undefined' && event.id !== null
      );
      const validPastEvents = (pastEventsResponse.data || []).filter(event => 
        event.id && event.id !== 'undefined' && event.id !== null
      );
      
      setEvents(validEvents);
      setPastEvents(validPastEvents);
    } catch (error) {
      // Silent fail
    }
  };

  // 피드백 데이터 가져오기 (Current + Past Events 모두 포함)
  const fetchFeedbackData = async () => {
    try {
      // 1. 모든 이벤트와 archived 이벤트 ID 목록 가져오기
      const [feedbackEventsResponse, archivedIdsResponse, adminEventsResponse] = await Promise.all([
        axios.get('/api/feedback/events'), // 피드백이 있는 이벤트들
        axios.get('/api/feedback/archived-events').catch(() => ({ data: [] })), // archived된 이벤트 ID 목록
        axios.get('/api/admin/events') // 현재 이벤트들
      ]);
      
      const allFeedbackEvents = feedbackEventsResponse.data || [];
      const archivedEventIds = new Set(archivedIdsResponse.data || []);
      const currentAdminEvents = adminEventsResponse.data || [];
      
      // 2. Past Events와 Current Events 분류
      const pastEventsData = allFeedbackEvents.filter(event => {
        const eventId = event.id || event.eventId;
        return eventId && archivedEventIds.has(eventId);
      }).filter(event => 
        event.id && event.id !== 'undefined' && event.id !== null
      );
      
      const currentEventsData = currentAdminEvents.filter(event => 
        event.id && event.id !== 'undefined' && event.id !== null
      );
      
      // ✅ 상태 변수에 저장! (Overall Feedback Trends가 사용함)
      setEvents(currentEventsData);
      setPastEvents(pastEventsData);
      
      // 3. 모든 이벤트 (Current + Past) 통합
      const allEvents = [...currentEventsData, ...pastEventsData];
      
      // 각 이벤트의 게임 피드백 데이터를 가져와서 통합
      const allGameFeedbacks = [];
      
      for (const event of allEvents) {
        // 이벤트 ID 유효성 검사
        if (!event.id || event.id === 'undefined' || event.id === null) {
          continue;
        }
        
        try {
          // 이벤트 통계 정보 가져오기 (실제 참여자 수)
          let actualAttendeeCount = event.attendeeCount || 0;
          try {
            const statisticsResponse = await axios.get(`/api/admin/events/${event.id}/statistics/pre-event`, {
              validateStatus: (status) => status < 500 // 4xx는 성공으로 처리하여 console.error 방지
            });
            if (statisticsResponse.status < 400 && statisticsResponse.data && !statisticsResponse.data.message) {
              actualAttendeeCount = statisticsResponse.data.currentAttendees || statisticsResponse.data.totalMembers || 0;
            } else {
              // Pre-event 스냅샷이 없으면 현재 통계 사용
              const currentStatsResponse = await axios.get(`/api/admin/events/${event.id}/statistics/current`, {
                validateStatus: (status) => status < 500
              });
              if (currentStatsResponse.status < 400) {
                actualAttendeeCount = currentStatsResponse.data.currentAttendees || currentStatsResponse.data.totalMembers || 0;
              }
            }
          } catch (error) {
            // 500대 에러만 여기서 처리 (4xx는 위에서 처리됨)
          }
          
          const gameResponse = await axios.get(`/api/feedback/event/${event.id}/games`);
          const gameData = gameResponse.data || [];
          
          // 각 게임의 상세 피드백 (태그 포함) 가져오기
          const detailedFeedbacks = [];
          try {
            const detailResponse = await axios.get(`/api/feedback/event/${event.id}/details`);
            if (detailResponse.data && Array.isArray(detailResponse.data)) {
              detailResponse.data.forEach(feedback => {
                if (feedback.gameRatings && typeof feedback.gameRatings === 'object') {
                  Object.keys(feedback.gameRatings).forEach(gameId => {
                    const gameRating = feedback.gameRatings[gameId]; // 이건 그냥 숫자 (rating)
                    
                    // gameNotes에서 positiveOptions, negativeOptions, customFeedback 추출
                    let positiveOptions = [];
                    let negativeOptions = [];
                    let customFeedbackText = '';
                    
                    if (feedback.gameNotes && feedback.gameNotes[gameId]) {
                      const gameNote = feedback.gameNotes[gameId];
                      if (Array.isArray(gameNote)) {
                        const positiveOptionsList = ['Fun & engaging', 'Helped me connect', 'Right duration'];
                        const negativeOptionsList = ['Boring', 'Group size issue (too many/few)', 'Confusing / unclear'];
                        
                        gameNote.forEach(note => {
                          if (positiveOptionsList.some(option => note.includes(option))) {
                            positiveOptions.push(note);
                          } else if (negativeOptionsList.some(option => note.includes(option))) {
                            negativeOptions.push(note);
                          } else {
                            // 태그가 아닌 실제 커스텀 피드백
                            customFeedbackText = note;
                          }
                        });
                      } else if (typeof gameNote === 'string') {
                        customFeedbackText = gameNote;
                      }
                    }
                    
                    detailedFeedbacks.push({
                      gameId: gameId,
                      rating: gameRating, // 평점
                      positiveOptions: positiveOptions,
                      negativeOptions: negativeOptions,
                      customFeedback: customFeedbackText
                    });
                  });
                }
              });
            }
          } catch (error) {
            // Silent fail
          }
          
          // 게임 데이터에 이벤트 정보 추가
          gameData.forEach(game => {
            // 해당 게임의 모든 상세 피드백 찾기
            const gameFeedbacks = detailedFeedbacks.filter(fb => {
              const fbGameId = fb.gameId?.toString().replace('game-', '').replace(/^0+/, '') || '0';
              const gameId = game.gameId?.toString().replace('game-', '').replace(/^0+/, '') || '0';
              return fbGameId === gameId;
            });
            
            allGameFeedbacks.push({
              gameId: game.gameId,
              rating: game.rating,
              responseCount: game.responseCount,
              customFeedbackCount: game.customFeedbackCount,
              eventId: event.id,
              eventTitle: event.title,
              eventDate: event.eventDateTime,
              attendeeCount: actualAttendeeCount, // 실제 참여자 수 (통계 API에서 가져옴)
              willAttendCount: actualAttendeeCount, // 실제 참여자 수
              branch: event.branch || 'Unknown',
              positiveOptions: gameFeedbacks.flatMap(fb => fb.positiveOptions),
              negativeOptions: gameFeedbacks.flatMap(fb => fb.negativeOptions),
              customFeedbacks: gameFeedbacks.map(fb => fb.customFeedback).filter(f => f)
            });
          });
        } catch (error) {
          // 게임 피드백이 없는 이벤트는 무시
        }
      }
      
      setFeedbackData(allGameFeedbacks);
    } catch (error) {
      setFeedbackData([]);
    }
  };

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/games');
      const gamesData = response.data || [];
      
      // 피드백 데이터를 기반으로 게임별 플레이 횟수와 평균 평점 계산
      const gamesWithStats = gamesData.map(game => {
        // ✅ game.gameId (String)를 사용해야 Feedback의 gameId와 매칭됨
        const gameId = game.gameId || game.id?.toString();
        const playedCount = calculateGamePlayCount(gameId);
        const averageRating = calculateGameAverageRating(gameId);
        
        return {
          ...game,
          playedCount,
          averageRating
        };
      });
      
      setGames(gamesWithStats);
    } catch (error) {
      // Silent fail
      setGames([
        {
          id: 1,
          name: 'Human Knot',
          description: 'A classic team building game where participants form a human knot and try to untangle themselves.',
          category: 'Team Building',
          minPlayers: 6,
          maxPlayers: 20,
          optimalPlayers: '8-12',
          difficultyLevel: 'medium',
          durationMinutes: 15,
          playedCount: 0,
          averageRating: 0
        },
        {
          id: 2,
          name: 'Two Truths One Lie',
          description: 'Each person shares three statements about themselves, two true and one false.',
          category: 'Icebreaker',
          minPlayers: 3,
          maxPlayers: 30,
          optimalPlayers: '5-15',
          difficultyLevel: 'easy',
          durationMinutes: 10,
          playedCount: 0,
          averageRating: 0
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // 폼 데이터 초기화
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      instructions: '',
      videoUrl: '',
      category: '',
      eventSize: 'SMALL',
      totalMinPlayers: 1,
      totalMaxPlayers: 40,
      recommendedTeamSize: '',
      difficultyLevel: 'medium',
      durationMinutes: ''
    });
    setEditingGame(null);
  };

  // 게임 생성/수정 제출
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 백엔드 API 형식에 맞게 데이터 변환
    const apiData = {
      name: formData.name,
      description: formData.description,
      instructions: formData.instructions,
      exampleVideoUrl: formData.videoUrl,
      category: formData.category,
      totalMinPlayers: formData.totalMinPlayers,        // 총 최소 인원
      totalMaxPlayers: formData.totalMaxPlayers,        // 총 최대 인원
      recommendedTeamSize: formData.recommendedTeamSize, // 추천 팀 인원
      estimatedDuration: formData.durationMinutes,
      difficulty: formData.difficultyLevel,
      active: formData.active
    };
    
    try {
      if (editingGame) {
        // 게임 수정 - 숫자 ID를 game-XX 형태로 변환
        const gameId = `game-${editingGame.id.toString().padStart(2, '0')}`;
        await axios.put(`/api/admin/games/${gameId}`, apiData);
        alert('Game updated successfully!');
      } else {
        // 게임 생성
        await axios.post('/api/admin/games', apiData);
        alert('Game created successfully!');
      }
      
      resetForm();
      setShowCreateForm(false);
      fetchGames();
      fetchFeedbackData(); // 피드백 데이터도 새로고침하여 통계 업데이트
    } catch (error) {
      alert('Failed to save game: ' + (error.response?.data?.message || error.message));
    }
  };

  // 게임 삭제 (ADMIN만)
  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    
    try {
      await axios.delete(`/api/admin/games/${gameId}`);
      alert('Game deleted successfully!');
      fetchGames();
      fetchFeedbackData(); // 피드백 데이터도 새로고침하여 통계 업데이트
    } catch (error) {
      alert('Failed to delete game: ' + (error.response?.data?.message || error.message));
    }
  };

  // 게임 편집 모드 시작
  const handleEditGame = (game) => {
    setEditingGame(game);
    
    // 기존 min/max 값으로 eventSize 결정
    const minPlayers = game.totalMinPlayers || game.minParticipants || 1;
    const maxPlayers = game.totalMaxPlayers || game.maxParticipants || 40;
    
    let eventSize = 'SMALL';
    if (minPlayers >= 60 || maxPlayers > 60) {
      eventSize = 'LARGE';
    } else if (minPlayers >= 40 || maxPlayers > 40) {
      eventSize = 'MEDIUM';
    }
    
    setFormData({
      name: game.name,
      description: game.description || '',
      instructions: game.instructions || '',
      videoUrl: game.exampleVideoUrl || '',
      category: game.category || '',
      eventSize: eventSize,
      totalMinPlayers: minPlayers,
      totalMaxPlayers: maxPlayers,
      recommendedTeamSize: game.recommendedTeamSize || game.optimalParticipants || '',
      difficultyLevel: game.difficulty || 'medium',
      durationMinutes: game.estimatedDuration || ''
    });
    setShowCreateForm(true);
  };

  // Event Size 변경 시 min/max 자동 설정
  const handleEventSizeChange = (eventSize) => {
    const selectedSize = EVENT_SIZE_OPTIONS.find(option => option.value === eventSize);
    if (selectedSize) {
      setFormData({
        ...formData,
        eventSize: eventSize,
        totalMinPlayers: selectedSize.min,
        totalMaxPlayers: selectedSize.max
      });
    }
  };

  // 필터링된 게임 목록
  const filteredGames = games.filter(game => {
    const matchesSearch = game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         game.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || game.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // 정렬된 게임 목록
  const sortedGames = [...filteredGames].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'category':
        aValue = a.category.toLowerCase();
        bValue = b.category.toLowerCase();
        break;
      case 'played':
        aValue = parseInt(a.playedCount) || 0;
        bValue = parseInt(b.playedCount) || 0;
        break;
      case 'lastPlayed':
        // 마지막 플레이 날짜로 정렬
        const getLastPlayedDate = (game) => {
          // ✅ game.gameId 사용
          const gameId = game.gameId || game.id?.toString();
          const gameFeedbacks = feedbackData.filter(fb => {
            if (!fb.gameId) return false;
            // ✅ gameId를 직접 문자열로 비교
            return String(gameId).trim() === String(fb.gameId).trim();
          });
          
          if (gameFeedbacks.length === 0) return new Date(0);
          
          return gameFeedbacks.reduce((latest, fb) => {
            const fbDate = new Date(fb.eventDate);
            return fbDate > latest ? fbDate : latest;
          }, new Date(0));
        };
        
        aValue = getLastPlayedDate(a).getTime();
        bValue = getLastPlayedDate(b).getTime();
        break;
      case 'rating':
        aValue = parseFloat(a.averageRating) || 0;
        bValue = parseFloat(b.averageRating) || 0;
        break;
      case 'performance':
        // Performance는 rating 기반으로 계산
        const getPerformanceScore = (rating) => {
          const r = parseFloat(rating) || 0;
          if (r >= 4.5) return 4;
          if (r >= 4.0) return 3;
          if (r >= 3.5) return 2;
          return 1;
        };
        aValue = getPerformanceScore(a.averageRating);
        bValue = getPerformanceScore(b.averageRating);
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // 게임별 플레이 횟수 계산
  const calculateGamePlayCount = (gameId) => {
    if (!gameId) return 0;
    
    const gameIdStr = String(gameId).trim();
    const count = feedbackData.filter(feedback => {
      if (!feedback.gameId) {
        return false;
      }
      
      // ✅ gameId를 직접 문자열로 비교 (둘 다 game.gameId이므로)
      const feedbackGameIdStr = String(feedback.gameId).trim();
      return gameIdStr === feedbackGameIdStr;
    }).length;
    
    return count;
  };

  // 게임별 평균 평점 계산
  const calculateGameAverageRating = (gameId) => {
    if (!gameId) return 0;
    
    const gameIdStr = String(gameId).trim();
    const gameFeedbacks = feedbackData.filter(feedback => {
      if (!feedback.gameId || !feedback.rating) {
        return false;
      }
      
      // ✅ gameId를 직접 문자열로 비교 (둘 다 game.gameId이므로)
      const feedbackGameIdStr = String(feedback.gameId).trim();
      return gameIdStr === feedbackGameIdStr;
    });
    
    if (gameFeedbacks.length === 0) {
      return 0;
    }
    
    const totalRating = gameFeedbacks.reduce((sum, feedback) => sum + parseFloat(feedback.rating || 0), 0);
    const averageRating = totalRating / gameFeedbacks.length;
    
    return parseFloat(averageRating.toFixed(1));
  };

  // 게임 통계 계산
  const getGameStats = () => {
    if (games.length === 0) return { totalGames: 0, avgRating: 0, totalFeedback: 0 };
    
    const totalGames = games.length;
    const totalFeedback = games.reduce((sum, game) => sum + (game.playedCount || 0), 0);
    const gamesWithRating = games.filter(game => game.averageRating && game.averageRating > 0);
    const avgRating = gamesWithRating.length > 0 
      ? gamesWithRating.reduce((sum, game) => sum + parseFloat(game.averageRating), 0) / gamesWithRating.length 
      : 0;
    
    return { totalGames, avgRating: avgRating.toFixed(1), totalFeedback };
  };

  // 필터링된 피드백 데이터 가져오기 (Game Analytics용)
  const getFilteredFeedbackData = () => {
    let filtered = feedbackData;
    
    // 유효한 이벤트 ID만 필터링
    const validEvents = [...events, ...pastEvents].filter(event => 
      event.id && event.id !== 'undefined' && event.id !== null
    );
    
    // Branch 필터링
    if (selectedBranch !== 'all') {
      filtered = filtered.filter(fb => {
        const event = validEvents.find(e => e.id === fb.eventId);
        return event && event.branch === selectedBranch;
      });
    }
    
    // 최근 N개 이벤트 필터링
    if (recentEventsCount !== 'all') {
      const count = parseInt(recentEventsCount);
      const allEvents = validEvents.sort((a, b) => 
        new Date(b.eventDateTime) - new Date(a.eventDateTime)
      );
      const recentEventIds = allEvents.slice(0, count).map(e => e.id);
      filtered = filtered.filter(fb => recentEventIds.includes(fb.eventId));
    }
    
    // 특정 이벤트 필터링
    if (selectedEventId !== 'all' && selectedEventId !== 'undefined') {
      const targetEventId = parseInt(selectedEventId);
      filtered = filtered.filter(fb => fb.eventId === targetEventId);
    }
    
    return filtered;
  };

  const stats = getGameStats();

  // 카테고리 접기/펴기 토글
  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Game Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Create, edit, and manage games for SLAM events</p>
        </div>

        {/* 탭 메뉴 */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex gap-6">
            <button 
              onClick={() => setActiveTab('games')} 
              className={`py-3 px-3 border-b-2 font-medium rounded-t-lg transition-all duration-200 ${
                activeTab === 'games' 
                  ? 'border-blue-500 text-blue-600 bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-25'
              }`}
            >
              🎮 Game List
            </button>
            <button 
              onClick={() => setActiveTab('analytics')} 
              className={`py-3 px-3 border-b-2 font-medium rounded-t-lg transition-all duration-200 ${
                activeTab === 'analytics' 
                  ? 'border-blue-500 text-blue-600 bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-25'
              }`}
            >
              🎯 Game Analytics
            </button>
          </nav>
        </div>

        {/* 통계 카드 */}
        {activeTab === 'games' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Games</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalGames}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.118 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Average Rating</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.avgRating}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Feedback</p>
                  <p className="text-2xl font-semibold text-gray-900">{stats.totalFeedback}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 컨트롤 바 */}
        {activeTab === 'games' && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                {/* 검색 */}
                <div className="relative flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Search games..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {/* 카테고리 필터 */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Categories</option>
                  {GAME_CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* 새 게임 생성 버튼 */}
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateForm(true);
                }}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create New Game
              </button>
            </div>
          </div>
        )}

        {/* 게임 목록 */}
        {activeTab === 'games' && (
          <>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading games...</p>
              </div>
            ) : isMobile ? (
              // Mobile Card View
              <div className="space-y-4">
                {sortedGames.map(game => {
                  // 마지막 플레이 날짜 계산
                  const gameIdStr = (game.gameId || game.id?.toString()).toString();
                  const gameFeedbacks = feedbackData.filter(fb => {
                    if (!fb.gameId) return false;
                    
                    const fbGameId = fb.gameId.toString();
                    
                    // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                    if (fbGameId === gameIdStr) return true;
                    
                    const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                    const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                    
                    return fbNumeric === gameNumeric;
                  });
                  
                  const lastPlayedDate = gameFeedbacks.length > 0
                    ? gameFeedbacks.reduce((latest, fb) => {
                        const fbDate = new Date(fb.eventDate);
                        return fbDate > latest ? fbDate : latest;
                      }, new Date(0))
                    : null;
                  
                  return (
                  <div key={game.id} className="bg-white p-4 rounded-lg shadow-md border">
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{game.name}</h3>
                        <p className="text-sm text-gray-600">{game.description}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {game.category}
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          game.difficultyLevel === 'easy' ? 'bg-green-100 text-green-800' :
                          game.difficultyLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {game.difficultyLevel}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Total Players:</span>
                          <span className="ml-1 font-medium">{game.totalMinPlayers || game.minPlayers}-{game.totalMaxPlayers || game.maxPlayers}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Duration:</span>
                          <span className="ml-1 font-medium">{game.durationMinutes}min</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Play Count:</span>
                          <span className="ml-1 font-medium text-blue-600">{game.playedCount || 0}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Last Played:</span>
                          <span className="ml-1 font-medium">
                            {lastPlayedDate && lastPlayedDate.getTime() > 0
                              ? (() => {
                                  const daysAgo = Math.floor((new Date() - lastPlayedDate) / (1000 * 60 * 60 * 24));
                                  if (daysAgo === 0) return 'Today';
                                  if (daysAgo === 1) return 'Yesterday';
                                  if (daysAgo < 7) return `${daysAgo}d ago`;
                                  if (daysAgo < 30) return `${Math.floor(daysAgo / 7)}w ago`;
                                  return lastPlayedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                })()
                              : 'Never'
                            }
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map(star => (
                              <svg
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= (parseFloat(game.averageRating) || 0) ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <span className="ml-2 text-sm text-gray-900">
                            {game.averageRating && parseFloat(game.averageRating) > 0 ? game.averageRating : 'N/A'}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleEditGame(game)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          Edit
                        </button>
                        {user?.role === 'ADMIN' && (
                          <button
                            onClick={() => handleDeleteGame(game.id)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
                {sortedGames.length === 0 && (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No games found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || selectedCategory ? 'Try adjusting your search or filter.' : 'Get started by creating a new game.'}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Desktop Table View
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200" style={{ minWidth: '1000px' }}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '200px' }}>
                          <button 
                            onClick={() => {
                              setSortField('name');
                              setSortOrder(sortField === 'name' && sortOrder === 'asc' ? 'desc' : 'asc');
                            }}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            Game
                            {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '120px' }}>
                          <button 
                            onClick={() => {
                              setSortField('category');
                              setSortOrder(sortField === 'category' && sortOrder === 'asc' ? 'desc' : 'asc');
                            }}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            Category
                            {sortField === 'category' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '100px' }}>
                          <button 
                            onClick={() => {
                              setSortField('played');
                              setSortOrder(sortField === 'played' && sortOrder === 'asc' ? 'desc' : 'asc');
                            }}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            Play Count
                            {sortField === 'played' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '120px' }}>
                          <button 
                            onClick={() => {
                              setSortField('lastPlayed');
                              setSortOrder(sortField === 'lastPlayed' && sortOrder === 'asc' ? 'desc' : 'asc');
                            }}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            Last Played
                            {sortField === 'lastPlayed' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '100px' }}>
                          <button 
                            onClick={() => {
                              setSortField('rating');
                              setSortOrder(sortField === 'rating' && sortOrder === 'asc' ? 'desc' : 'asc');
                            }}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            Rating
                            {sortField === 'rating' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-2" style={{ width: '120px' }}>
                          <button 
                            onClick={() => {
                              setSortField('performance');
                              setSortOrder(sortField === 'performance' && sortOrder === 'asc' ? 'desc' : 'asc');
                            }}
                            className="flex items-center gap-1 hover:text-gray-700"
                          >
                            Performance
                            {sortField === 'performance' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </button>
                          <button 
                            onClick={() => setShowPerformanceCriteria(!showPerformanceCriteria)}
                            className="text-blue-500 hover:text-blue-700 text-sm"
                            title="Rating Criteria"
                          >
                            ℹ️
                          </button>
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '150px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedGames.map(game => {
                        // ✅ 해당 게임의 마지막 플레이 날짜 계산 - game.gameId 사용
                        const gameId = game.gameId || game.id?.toString();
                        const gameFeedbacks = feedbackData.filter(fb => {
                          if (!fb.gameId) return false;
                          // ✅ gameId를 직접 문자열로 비교
                          return String(gameId).trim() === String(fb.gameId).trim();
                        });
                        
                        const lastPlayedDate = gameFeedbacks.length > 0
                          ? gameFeedbacks.reduce((latest, fb) => {
                              const fbDate = new Date(fb.eventDate);
                              return fbDate > latest ? fbDate : latest;
                            }, new Date(0))
                          : null;
                        
                        return (
                        <tr key={game.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 whitespace-nowrap" style={{ width: '200px' }}>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{game.name}</div>
                              <div className="text-xs text-gray-500 max-w-xs truncate">{game.description}</div>
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap" style={{ width: '120px' }}>
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {game.category}
                            </span>
                          </td>
      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900" style={{ width: '100px' }}>
        <div className="text-lg font-semibold text-blue-600">
          {game.playedCount || 0}
        </div>
        <div className="text-xs text-gray-500">
          {(() => {
            // ✅ 해당 게임의 총 투표자 수 계산 - game.gameId 사용
            const gameId = game.gameId || game.id?.toString();
            const totalVoters = feedbackData
              .filter(feedback => {
                if (!feedback.gameId) return false;
                // ✅ gameId를 직접 문자열로 비교
                return String(gameId).trim() === String(feedback.gameId).trim();
              })
              .reduce((sum, feedback) => sum + (feedback.responseCount || 0), 0);
            
            return totalVoters > 0 ? `${totalVoters} voters` : '0 voters';
          })()}
        </div>
        {game.recommendedTeamSize && (
          <div className="text-xs text-gray-500">Team Size: {game.recommendedTeamSize}</div>
        )}
      </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900" style={{ width: '120px' }}>
                            {lastPlayedDate && lastPlayedDate.getTime() > 0 ? (
                              <div>
                                <div className="font-medium text-gray-900 text-xs">
                                  {lastPlayedDate.toLocaleDateString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {(() => {
                                    const daysAgo = Math.floor((new Date() - lastPlayedDate) / (1000 * 60 * 60 * 24));
                                    if (daysAgo === 0) return 'Today';
                                    if (daysAgo === 1) return 'Yesterday';
                                    if (daysAgo < 7) return `${daysAgo} days ago`;
                                    if (daysAgo < 30) return `${Math.floor(daysAgo / 7)} weeks ago`;
                                    if (daysAgo < 365) return `${Math.floor(daysAgo / 30)} months ago`;
                                    return `${Math.floor(daysAgo / 365)} years ago`;
                                  })()}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 italic text-xs">Never played</span>
        )}
      </td>
                          <td className="px-3 py-3 whitespace-nowrap" style={{ width: '100px' }}>
                            <div className="flex items-center">
                              <div className="flex items-center">
                                {[1, 2, 3, 4, 5].map(star => (
                                  <svg
                                    key={star}
                                    className={`w-3 h-3 ${
                                      star <= (parseFloat(game.averageRating) || 0) ? 'text-yellow-400' : 'text-gray-300'
                                    }`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                ))}
                              </div>
                              <span className="ml-1 text-xs text-gray-900">
                                {game.averageRating && parseFloat(game.averageRating) > 0 ? game.averageRating : 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900" style={{ width: '120px' }}>
                            <div className="text-xs">
                              {(() => {
                                const rating = parseFloat(game.averageRating) || 0;
                                if (rating >= 4.5) return '🔥 Excellent';
                                if (rating >= 4.0) return '👍 Good';
                                if (rating >= 3.5) return '👌 Average';
                                return '⚠️ Needs Improvement';
                              })()}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm font-medium" style={{ width: '150px' }}>
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleEditGame(game)}
                                className="text-blue-600 hover:text-blue-900 text-xs"
                              >
                                Edit
                              </button>
                              {user?.role === 'ADMIN' && (
                                <button
                                  onClick={() => handleDeleteGame(game.id)}
                                  className="text-red-600 hover:text-red-900 text-xs"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {sortedGames.length === 0 && (
                  <div className="text-center py-12">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No games found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || selectedCategory ? 'Try adjusting your search or filter.' : 'Get started by creating a new game.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Performance 기준 모달 */}
        {showPerformanceCriteria && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Performance Rating Criteria</h2>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🔥</span>
                    <div>
                      <div className="font-semibold text-green-600">Excellent (4.5+)</div>
                      <div className="text-sm text-gray-600">Outstanding performance, highly recommended</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">👍</span>
                    <div>
                      <div className="font-semibold text-blue-600">Good (4.0-4.4)</div>
                      <div className="text-sm text-gray-600">Good performance, recommended</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">👌</span>
                    <div>
                      <div className="font-semibold text-yellow-600">Average (3.5-3.9)</div>
                      <div className="text-sm text-gray-600">Average performance, acceptable</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <div className="font-semibold text-red-600">Needs Improvement (Below 3.5)</div>
                      <div className="text-sm text-gray-600">Below average, needs improvement</div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowPerformanceCriteria(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 게임 생성/수정 모달 */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-6">
                  {editingGame ? 'Edit Game' : 'Create New Game'}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 기본 정보 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Game Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Human Knot, Two Truths One Lie"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Category</option>
                        {GAME_CATEGORIES.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* 참가자 수 및 난이도 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Suitable Event Size *
                      </label>
                      <select
                        value={formData.eventSize}
                        onChange={(e) => handleEventSizeChange(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {EVENT_SIZE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        This game works best for events with {formData.totalMinPlayers}-{formData.totalMaxPlayers} attendees
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Recommended Team Size
                      </label>
                      <input
                        type="text"
                        value={formData.recommendedTeamSize}
                        onChange={(e) => setFormData({...formData, recommendedTeamSize: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 6-8 people"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Difficulty Level
                      </label>
                      <select
                        value={formData.difficultyLevel}
                        onChange={(e) => setFormData({...formData, difficultyLevel: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {DIFFICULTY_LEVELS.map(level => (
                          <option key={level.value} value={level.value}>{level.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* 소요 시간 및 영상 링크 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration (minutes)
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={formData.durationMinutes}
                        onChange={(e) => setFormData({...formData, durationMinutes: parseInt(e.target.value)})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., 15-20"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Video URL (Optional)
                      </label>
                      <input
                        type="url"
                        value={formData.videoUrl}
                        onChange={(e) => setFormData({...formData, videoUrl: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="YouTube, Vimeo, or other video platform URL"
                      />
                    </div>
                  </div>

                  {/* 게임 설명 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Game Description
                    </label>
                    <textarea
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Brief description of what the game is about..."
                    />
                  </div>

                  {/* 게임 방법 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How to Play (Instructions)
                    </label>
                    <textarea
                      rows="6"
                      value={formData.instructions}
                      onChange={(e) => setFormData({...formData, instructions: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Step-by-step instructions on how to play the game..."
                    />
                  </div>

                  {/* 버튼 */}
                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        resetForm();
                      }}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {editingGame ? 'Update Game' : 'Create Game'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

                {/* Game Analytics Tab Content */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* 1. Popularity Ranking - 인기 점수 기준 (맨 위로 이동) */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">🏆 Top 5 Popular Games</h3>
              <p className="text-sm text-gray-600 mb-4">
                Most validated games based on popularity score (Rating × Total Votes) - No maximum limit, higher is better
              </p>
                <div className="space-y-3">
                {(() => {
                  // 인기 점수 = (평균 평점) x (총 투표 수) 계산
                  const gamesWithPopularity = games
                    .map(game => {
                      const rating = parseFloat(game.averageRating) || 0;
                      const votes = game.playedCount || 0;
                      const popularityScore = rating * votes;
                      return { ...game, popularityScore, rating, votes };
                    })
                    .filter(game => game.popularityScore > 0)
                    .sort((a, b) => b.popularityScore - a.popularityScore)
                    .slice(0, 5);

                  if (gamesWithPopularity.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        No game feedback data available yet
                      </div>
                    );
                  }

                  return gamesWithPopularity.map((game, index) => (
                    <div key={game.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-4 flex-1">
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-bold ${
                          index === 0 ? 'bg-yellow-400 text-yellow-900 shadow-lg' :
                            index === 1 ? 'bg-gray-300 text-gray-700' :
                            index === 2 ? 'bg-orange-400 text-orange-900' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                          </span>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{game.name}</div>
                          <div className="text-sm text-gray-500">{game.category}</div>
                          </div>
                        </div>
                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{game.popularityScore.toFixed(1)}</div>
                          <div className="text-xs text-gray-500">Popularity Score</div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center">
                            <span className="text-lg font-semibold text-gray-900">{game.rating.toFixed(1)}</span>
                            <span className="text-yellow-400 ml-1">★</span>
                      </div>
                          <div className="text-xs text-gray-500">{game.votes} votes</div>
                        </div>
                      </div>
                    </div>
                  ));
                })()}
                </div>
              </div>

            {/* 2. Best Games by Event Size - 총 이벤트 참여 인원 기준 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">👥 Best Games by Event Size</h3>
              <p className="text-sm text-gray-600 mb-4">
                Game performance based on <strong>total event attendance</strong>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { label: 'Small Event (~40 people)', min: 1, max: 40, emoji: '👥' },
                  { label: 'Medium Event (40-60 people)', min: 40, max: 60, emoji: '👨‍👩‍👧‍👦' },
                  { label: 'Large Event (60+ people)', min: 60, max: 999, emoji: '🎪' }
                ].map(group => {
                  // 총 이벤트 참여 인원 기준으로 필터링
                  const gamesInGroup = games
                    .map(game => {
                      const gameIdStr = (game.gameId || game.id?.toString()).toString();
                      
                      // 해당 게임의 피드백 데이터에서 이벤트 크기별 평점 계산
                      const eventSizeFeedbacks = getFilteredFeedbackData().filter(fb => {
                        if (!fb.gameId) return false;
                        
                        const fbGameId = fb.gameId.toString();
                        
                        // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                        if (fbGameId === gameIdStr) return true;
                        
                        const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                        const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                        
                        return fbNumeric === gameNumeric;
                      }).filter(fb => {
                        // 이벤트 총 참여 인원이 해당 그룹 범위에 맞는지 확인
                        const totalAttendance = fb.willAttendCount || fb.attendeeCount || 0;
                        return totalAttendance >= group.min && totalAttendance <= group.max;
                      });
                      
                      if (eventSizeFeedbacks.length === 0) {
                        return null; // 해당 이벤트 크기에서 플레이되지 않은 게임
                      }
                      
                      // 해당 이벤트 크기에서의 평균 평점 계산
                      const totalRating = eventSizeFeedbacks.reduce((sum, fb) => sum + parseFloat(fb.rating || 0), 0);
                      const avgRating = totalRating / eventSizeFeedbacks.length;
                      const playCount = eventSizeFeedbacks.length;
                      
                      return { 
                        ...game, 
                        avgRating, 
                        playCount,
                        eventSizeFeedbacks,
                        // 인기 점수로 정렬 (평점이 높고 플레이 횟수도 많은 게임)
                        popularityScore: avgRating * Math.log(playCount + 1)
                      };
                    })
                    .filter(game => game !== null && game.playCount > 0) // 데이터가 있는 게임만
                    .sort((a, b) => {
                      // 먼저 평점으로 정렬, 같으면 플레이 횟수로 정렬
                      if (Math.abs(b.avgRating - a.avgRating) > 0.1) {
                        return b.avgRating - a.avgRating;
                      }
                      return b.playCount - a.playCount;
                    })
                    .slice(0, 5); // Top 5로 확장

                  // 해당 이벤트 크기에서 플레이된 게임 수 계산
                  const totalGamesInThisSize = getFilteredFeedbackData().filter(fb => {
                    const totalAttendance = fb.willAttendCount || fb.attendeeCount || 0;
                    return totalAttendance >= group.min && totalAttendance <= group.max;
                  }).length;
                     
                    return (
                    <div key={group.label} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <span className="text-xl mr-2">{group.emoji}</span>
                        {group.label}
                      </h4>
                      <p className="text-xs text-gray-500 mb-3">{totalGamesInThisSize} events in this size</p>
                      
                      <div className="space-y-2">
                        {gamesInGroup.length === 0 ? (
                          <div className="text-sm text-gray-500 italic py-4 text-center">
                            No games played in {group.label.toLowerCase()} yet
                          </div>
                        ) : (
                          gamesInGroup.map((game, idx) => (
                            <div key={game.id} className="p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `${idx + 1}.`} {game.name}
                                </span>
                                {game.avgRating > 0 && (
                                  <div className="flex items-center">
                                    <span className="text-sm font-bold text-blue-600">{game.avgRating.toFixed(1)}</span>
                                    <span className="text-yellow-400 ml-1">★</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>
                                  Team Size: {game.recommendedTeamSize || game.optimalParticipants || game.optimalPlayers || 'N/A'}
                                </span>
                                {game.playCount > 0 && (
                                  <span className="text-blue-600">
                                    {game.playCount} event{game.playCount > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            {/* 3. Feedback Trend Analysis - AdminFeedbackPage 스타일로 개선 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">🏷️ Overall Feedback Trends</h3>
              <p className="text-sm text-gray-600 mb-4">Aggregated feedback across all games</p>
              
              {/* 필터 컨트롤 */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 1. Branch 필터 (먼저) */}
              <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🏢 Branch
                    </label>
                    <select
                      value={selectedBranch}
                      onChange={(e) => {
                        setSelectedBranch(e.target.value);
                        setSelectedEventId('all'); // Branch 변경 시 Specific Event 초기화
                      }}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Branches</option>
                      <option value="NCCU">NCCU</option>
                      <option value="NTU">NTU</option>
                      <option value="Taipei">Taipei</option>
                    </select>
                          </div>
                  
                  {/* 2. Specific Event (Branch로 필터링됨) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      🎯 Specific Event
                    </label>
                    <select
                      value={selectedEventId}
                      onChange={(e) => setSelectedEventId(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Events</option>
                      {(() => {
                        // 중복 제거 및 Branch 필터링
                        const allEvents = [...events, ...pastEvents];
                        const uniqueEvents = Array.from(
                          new Map(allEvents.map(event => [event.id, event])).values()
                        );
                        
                        // Branch 필터 적용
                        const filteredByBranch = selectedBranch === 'all' 
                          ? uniqueEvents 
                          : uniqueEvents.filter(e => e.branch === selectedBranch);
                        
                        return filteredByBranch
                          .sort((a, b) => new Date(b.eventDateTime) - new Date(a.eventDateTime))
                          .map(event => (
                            <option key={event.id} value={event.id}>
                              {event.title} - {new Date(event.eventDateTime).toLocaleDateString()}
                            </option>
                          ));
                      })()}
                    </select>
                        </div>
                  
                  {/* 3. Recent Events (마지막) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      📅 Recent Events
                    </label>
                    <select
                      value={recentEventsCount}
                      onChange={(e) => setRecentEventsCount(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Events</option>
                      <option value="3">Last 3 Events</option>
                      <option value="5">Last 5 Events</option>
                      <option value="10">Last 10 Events</option>
                    </select>
                        </div>
                </div>
                
                <div className="mt-3 text-sm text-gray-600">
                  {(() => {
                    const filteredData = getFilteredFeedbackData();
                    const validEvents = [...events, ...pastEvents].filter(event => 
                      event.id && event.id !== 'undefined' && event.id !== null
                    );
                    return `Showing ${filteredData.length} feedback entries from ${validEvents.length} total events`;
                  })()}
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Positive Feedback */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                    <span className="text-xl mr-2">👍</span>
                    Positive Feedback
                  </h4>
                <div className="space-y-3">
                    {['Fun & engaging', 'Helped me connect', 'Right duration'].map(tag => {
                      // ✅ 필터링된 Past Events 데이터 사용
                      const validPastEvents = pastEvents.filter(event => 
                        event.id && event.id !== 'undefined' && event.id !== null
                      );
                      const pastEventIds = validPastEvents.map(e => e.id);
                      
                      // 전체 피드백에서 필터 적용 후 Past Events만 추출
                      const filteredData = getFilteredFeedbackData();
                      const pastFeedbackData = filteredData.filter(fb => pastEventIds.includes(fb.eventId));
                      
                      const tagCount = pastFeedbackData.filter(fb => 
                        fb.positiveOptions && fb.positiveOptions.includes(tag)
                      ).length;
                      
                      const totalFeedbacks = pastFeedbackData.length;
                      const percentage = totalFeedbacks > 0 ? (tagCount / totalFeedbacks) * 100 : 0;
                      
                      return (
                        <div key={tag} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-green-800 font-medium">{tag}</span>
                            <span className="text-green-600">{tagCount} ({percentage.toFixed(0)}%)</span>
                          </div>
                          <div className="w-full bg-green-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
              </div>

                {/* Negative Feedback */}
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                    <span className="text-xl mr-2">👎</span>
                    Negative Feedback
                  </h4>
                  <div className="space-y-3">
                    {['Boring', 'Group size issue (too many/few)', 'Confusing / unclear'].map(tag => {
                      // ✅ 필터링된 Past Events 데이터 사용
                      const validPastEvents = pastEvents.filter(event => 
                        event.id && event.id !== 'undefined' && event.id !== null
                      );
                      const pastEventIds = validPastEvents.map(e => e.id);
                      
                      // 전체 피드백에서 필터 적용 후 Past Events만 추출
                      const filteredData = getFilteredFeedbackData();
                      const pastFeedbackData = filteredData.filter(fb => pastEventIds.includes(fb.eventId));
                      
                      const tagCount = pastFeedbackData.filter(fb => 
                        fb.negativeOptions && fb.negativeOptions.includes(tag)
                      ).length;
                      
                      const totalFeedbacks = pastFeedbackData.length;
                      const percentage = totalFeedbacks > 0 ? (tagCount / totalFeedbacks) * 100 : 0;
                      
                      return (
                        <div key={tag} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-red-800 font-medium">{tag}</span>
                            <span className="text-red-600">{tagCount} ({percentage.toFixed(0)}%)</span>
                          </div>
                          <div className="w-full bg-red-200 rounded-full h-2">
                            <div 
                              className="bg-red-600 h-2 rounded-full transition-all duration-300" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              {/* Custom Feedback - 아래로 이동, 메달 제거 */}
              <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div 
                  className="flex items-center justify-between mb-3 cursor-pointer hover:bg-gray-100 -m-4 p-4 rounded-t-lg transition-colors"
                  onClick={() => setCollapsedCategories(prev => ({ ...prev, customFeedback: !prev.customFeedback }))}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg transition-transform duration-200" style={{ transform: collapsedCategories.customFeedback ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                      📝
                    </span>
                    <h4 className="font-semibold text-gray-800">Custom Feedback</h4>
                  </div>
                  <span className="text-sm text-gray-500">
                    {collapsedCategories.customFeedback ? 'Click to expand' : 'Click to collapse'}
                  </span>
                </div>
                
                {!collapsedCategories.customFeedback && (
                  <div className="space-y-3">
                  {(() => {
                    // ✅ 필터링된 Past Events 데이터 사용
                    const validPastEvents = pastEvents.filter(event => 
                      event.id && event.id !== 'undefined' && event.id !== null
                    );
                    const pastEventIds = validPastEvents.map(e => e.id);
                    
                    // 전체 피드백에서 필터 적용 후 Past Events만 추출
                    const filteredData = getFilteredFeedbackData();
                    const pastFeedbackData = filteredData.filter(fb => pastEventIds.includes(fb.eventId));
                    
                    // 모든 Custom Feedback을 게임별로 그룹화
                    const gamesWithComments = games.map(game => {
                      const gameIdStr = (game.gameId || game.id?.toString()).toString();
                      const customFeedbacks = pastFeedbackData.filter(fb => {
                        if (!fb.gameId) return false;
                        
                        const fbGameId = fb.gameId.toString();
                        
                        // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                        if (fbGameId === gameIdStr) {
                          return fb.customFeedbacks && fb.customFeedbacks.length > 0;
                        }
                        
                        const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                        const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                        
                        return fbNumeric === gameNumeric && fb.customFeedbacks && fb.customFeedbacks.length > 0;
                      }).flatMap(fb => fb.customFeedbacks);
                      
                      return { ...game, customFeedbacks, commentCount: customFeedbacks.length };
                    }).filter(g => g.commentCount > 0)
                      .sort((a, b) => b.commentCount - a.commentCount);
                    
                    if (gamesWithComments.length === 0) {
                      return <div className="text-sm text-gray-600 italic text-center py-4">No custom feedback yet</div>;
                    }
                    
                    // Top 10 게임 표시
                    return gamesWithComments.slice(0, 10).map((game, idx) => (
                      <div key={game.id} className="bg-white p-3 rounded-lg border border-gray-300 hover:border-gray-400 transition-colors">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm">▶</span>
                            <div className="font-medium text-gray-900">
                              {idx + 1}. {game.name}
                            </div>
                          </div>
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                            {game.commentCount} comment{game.commentCount > 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="space-y-2 pl-4">
                          {game.customFeedbacks.slice(0, 3).map((feedback, fbIdx) => (
                            <div key={fbIdx} className="text-sm text-gray-700 bg-gray-50 p-2 rounded border-l-2 border-gray-400">
                              "{feedback}"
                      </div>
                    ))}
                          {game.customFeedbacks.length > 3 && (
                            <details className="group">
                              <summary className="cursor-pointer text-xs text-blue-600 hover:text-blue-800 font-medium">
                                +{game.customFeedbacks.length - 3} more comments
                              </summary>
                              <div className="mt-2 space-y-2">
                                {game.customFeedbacks.slice(3).map((feedback, fbIdx) => (
                                  <div key={fbIdx + 3} className="text-sm text-gray-700 bg-gray-50 p-2 rounded border-l-2 border-gray-400">
                                    "{feedback}"
                                  </div>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    ));
                  })()}
                  </div>
                )}
              </div>
            </div>


            {/* 5. Category Performance - Detailed View */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">📊 Category Performance Detail</h3>
              <p className="text-sm text-gray-600 mb-4">Detailed play history and feedback by category</p>
              
              <div className="space-y-6">
                  {GAME_CATEGORIES.map(category => {
                  const categoryGames = games.filter(game => game.category === category);
                     
                    return (
                    <div key={category} className="border border-gray-200 rounded-lg p-6 bg-gradient-to-r from-gray-50 to-white">
                      <div 
                        className="flex items-center justify-between mb-4 cursor-pointer hover:bg-gray-100 -m-6 p-4 rounded-t-lg transition-colors"
                        onClick={() => toggleCategory(category)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg transition-transform duration-200" style={{ transform: collapsedCategories[category] ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                            {category === 'Icebreaking' ? '🧊' : '🗣️'}
                          </span>
                          <h4 className="font-semibold text-gray-900 text-base">{category}</h4>
                        </div>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {categoryGames.length} games
                        </span>
                      </div>
                      
                      {!collapsedCategories[category] && (
                      
                      <div className="space-y-4">
                        {categoryGames.map(game => {
                          const gameIdStr = (game.gameId || game.id?.toString()).toString();
                          
                          // 해당 게임의 모든 플레이 기록 (중복 제거)
                          const rawPlayHistory = feedbackData.filter(fb => {
                            if (!fb.gameId) return false;
                            
                            const fbGameId = fb.gameId.toString();
                            
                            // 정확히 일치하거나, game-01 형태를 숫자로 변환해서 비교
                            if (fbGameId === gameIdStr) return true;
                            
                            const fbNumeric = fbGameId.replace('game-', '').replace(/^0+/, '') || '0';
                            const gameNumeric = gameIdStr.replace('game-', '').replace(/^0+/, '') || '0';
                            
                            return fbNumeric === gameNumeric;
                          });
                          
                          // 이벤트별로 중복 제거 (같은 이벤트에서 여러 번 플레이된 경우 하나만 표시)
                          const playHistoryMap = new Map();
                          rawPlayHistory.forEach(play => {
                            const key = `${play.eventId}-${play.eventTitle}`;
                            if (!playHistoryMap.has(key) || play.rating > (playHistoryMap.get(key).rating || 0)) {
                              playHistoryMap.set(key, play);
                            }
                          });
                          const playHistory = Array.from(playHistoryMap.values());
                          
                          if (playHistory.length === 0) {
                            return (
                              <div key={game.id} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="font-semibold text-gray-900">{game.name}</div>
                                    <div className="text-sm text-gray-500 mt-1">Not played yet</div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm text-gray-500">Team Size: {game.recommendedTeamSize || game.optimalParticipants || game.optimalPlayers || 'N/A'}</div>
                                    <div className="text-sm text-gray-500">Total Range: {game.totalMinPlayers || game.minParticipants || game.minPlayers}-{game.totalMaxPlayers || game.maxParticipants || game.maxPlayers}</div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          
                          // 마지막 플레이 날짜
                          const lastPlayed = playHistory.reduce((latest, fb) => {
                            const fbDate = new Date(fb.eventDate);
                            return fbDate > latest ? fbDate : latest;
                          }, new Date(0));
                          
                          return (
                            <div key={game.id} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <div className="font-semibold text-gray-900 text-lg mb-2">{game.name}</div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div className="flex items-center">
                                      <span className="text-gray-500 mr-2">📅</span>
                                      <span className="font-medium">{lastPlayed.toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center">
                                      <span className="text-gray-500 mr-2">🎮</span>
                                      <span className="font-medium">{game.playedCount || 0} times</span>
                                    </div>
                                    <div className="flex items-center">
                                      <span className="text-yellow-500 mr-2">⭐</span>
                                      <span className="font-medium">{game.averageRating ? Number(game.averageRating).toFixed(1) : 'N/A'}</span>
                                      {game.averageRating && <span className="text-gray-500 ml-1">({game.playedCount} votes)</span>}
                                    </div>
                                    <div className="flex items-center">
                                      <span className="text-gray-500 mr-2">👥</span>
                                      <span className="font-medium">{game.recommendedTeamSize || game.optimalParticipants || game.optimalPlayers || 'N/A'}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Play History - 테이블 형태로 개선 */}
                              <div className="mt-4">
                                <h5 className="text-sm font-semibold text-gray-700 mb-3">Play History</h5>
                                <div className="overflow-x-auto">
                                  <table className="w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Participants</th>
                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
                                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Feedback</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                      {playHistory.map((play, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                          <td className="px-2 py-1 text-xs font-medium text-gray-900 truncate max-w-32">
                                            {play.eventTitle}
                                          </td>
                                          <td className="px-2 py-1 text-xs text-gray-500">
                                            {new Date(play.eventDate).toLocaleDateString()}
                                          </td>
                                          <td className="px-2 py-1 text-xs text-gray-500">
                                            <div className="flex flex-col">
                                              <span className="font-medium text-blue-600">
                                                {play.willAttendCount || play.attendeeCount || 'Unknown'}
                                              </span>
                                              <span className="text-xs text-gray-400">
                                                {play.responseCount || 0} responses
                                              </span>
                                            </div>
                                          </td>
                                          <td className="px-2 py-1 text-xs text-gray-500">
                                            <div className="flex items-center">
                                              <span className="text-yellow-500 mr-1">★</span>
                                              <span className="font-medium">{play.rating ? Number(play.rating).toFixed(1) : 'N/A'}</span>
                                            </div>
                                          </td>
                                          <td className="px-2 py-1 text-xs text-gray-500">
                                            <span className="px-1 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                                              {play.branch || 'Unknown'}
                                            </span>
                                          </td>
                                          <td className="px-2 py-1 text-xs text-gray-500">
                                            <div className="flex flex-wrap gap-1">
                                              {play.tags && play.tags.slice(0, 2).map((tag, tagIdx) => (
                                                <span key={tagIdx} className="px-1 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                                  {tag}
                                                </span>
                                              ))}
                                              {play.tags && play.tags.length > 2 && (
                                                <span className="px-1 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                  +{play.tags.length - 2}
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
